import { Link } from '@/components/Elements/Link';
import { FilterCondition } from '@/features/analyze/types';
import { Theme } from '@/features/theme/types';
import { useTranslationDictionary } from '@/lib/i18n/useTranslationDictionary';
import { useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';

type ThemeCategoryCardProps = {
  title: string;
  iconImage: string;
  coverImage: string;
  themes: Theme[];
};

const CategoryTitle = ({
  title,
  iconImage,
}: {
  title: string;
  iconImage: string;
}) => (
  <div className="flex h-16 items-center gap-3 rounded-tr-xl text-xl font-medium leading-5">
    <img alt={title} src={iconImage} className="size-8" />
    {title}
  </div>
);

const themeList = tv({
  base: 'absolute bottom-16 left-0 max-h-60 w-full overflow-y-hidden scroll-smooth rounded-tr-xl bg-white/90 px-4 transition-transform duration-500 ease-in-out',
  variants: {
    isDisplay: {
      true: 'translate-y-16 overflow-y-auto',
      false: 'translate-y-full',
    },
  },
});

export const ThemeCategoryCard = ({
  title,
  iconImage,
  coverImage,
  themes,
}: ThemeCategoryCardProps) => {
  const [isDisplay, setIsDisplay] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { getLabel: getThemeLabel } = useTranslationDictionary('theme');

  // テーマリストをスクロールしてた際にhover解除時にスクロールをトップに戻す
  // 必ず要素に scroll-smooth クラスを付与すること
  useEffect(() => {
    if (!isDisplay && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isDisplay]);

  // テーマ検索用のURL作成関数
  const createThemeSearchUrl = (themeTableName: string) => {
    const filterCondition: FilterCondition = {
      themes: { and: [themeTableName] },
    };
    const serializedCondition = JSON.stringify(filterCondition);
    return `/analyze/search#q=${encodeURIComponent(serializedCondition)}`;
  };

  return (
    <div
      className="relative overflow-hidden rounded-tr-xl"
      onMouseEnter={() => setIsDisplay(true)}
      onMouseLeave={() => setIsDisplay(false)}
      onClick={() => setIsDisplay(!isDisplay)} // タッチデバイス対応
    >
      <img src={coverImage} alt={title} className="h-64 w-full object-cover" />

      {/* hover時に表示する */}
      <div className={themeList({ isDisplay })} ref={scrollRef}>
        <CategoryTitle title={title} iconImage={iconImage} />
        <ul className="m-1">
          {themes.map((theme) => (
            <li key={theme.id} className="text-base text-gray-700">
              <Link
                to={createThemeSearchUrl(theme.tableName)}
                className="block w-full p-1 transition-colors duration-200"
                underline="hover"
              >
                {getThemeLabel(theme.tableName)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
